import { inject, Pipe, type PipeTransform } from '@angular/core';
import { LocalizationService } from '@services/logic/localization.service';
/**
 * Formats a major-unit amount with the correct currency symbol and locale.
 *
 * Usage: {{ amount | formatCurrency }}
 * Example: {{ 2222 | formatCurrency }} → "2.222 kr."  (handyhand.dk)
 * Example: {{ 1234 | formatCurrency }} → "1.234 €"    (handyhand.nl)
 *
 * Input is in MAJOR units. API amounts stored in the smallest currency unit
 * (e.g. `offer.priceBid`) must be divided in the template expression
 * (`value / 100 | formatCurrency`) — the pipe never divides, so a
 * pre-converted amount is not converted twice.
 *
 * The currency defaults to the host country's (`LocalizationService.
 * currencyCode`); pass one explicitly only to override, e.g.
 * {{ amount | formatCurrency: 'eur' }}. Lower-case codes are accepted
 * (matches Stripe / LocalizationService). The locale is read from
 * `LocalizationService.bcp47` (populated from the API).
 *
 * Null, undefined, and non-numeric input render as '' — never "NaN kr."
 * (NaN in visible text has shipped before).
 *
 * `pure: false` — re-evaluates after the async localization-config API fetch
 * settles, same pattern as TranslatePipe.
 */
@Pipe({
  name: 'formatCurrency',
  pure: false,
})
export class FormatCurrencyPipe implements PipeTransform {
  private readonly localizationService = inject(LocalizationService);

  transform(
    value: number | string | null | undefined,
    currency?: string | null,
  ): string {
    if (value === null || value === undefined) {
      return '';
    }

    const amount = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(amount)) {
      return '';
    }

    const resolvedCurrency =
      currency ?? this.localizationService.currencyCode();

    if (!resolvedCurrency) {
      return String(amount);
    }

    const bcp47 = this.localizationService.bcp47();

    try {
      return new Intl.NumberFormat(bcp47, {
        style: 'currency',
        currency: resolvedCurrency.toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${amount} ${resolvedCurrency}`;
    }
  }
}
